import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  ListGroup,
  Badge,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaCheckCircle, FaArrowRight, FaShoppingBag } from "react-icons/fa";

const OrderSuccessPage = () => {
  const [orderData, setOrderData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 로컬 스토리지에서 최근 주문 데이터 가져오기
    const savedOrderData = localStorage.getItem("latestOrder");
    
    if (!savedOrderData) {
      // 주문 데이터가 없으면 홈으로 리다이렉트
      navigate("/");
      return;
    }
    
    try {
      const parsedOrder = JSON.parse(savedOrderData);
      setOrderData(parsedOrder);
      
      // 주문 데이터를 orders 배열에 저장
      const savedOrders = JSON.parse(localStorage.getItem("orders") || "[]");
      
      // 같은 주문이 있는지 확인 (receipt_id로 체크)
      const orderExists = savedOrders.some(order => 
        order.receipt_id === parsedOrder.receipt_id
      );
      
      if (!orderExists) {
        // 새 주문 추가
        const updatedOrders = [parsedOrder, ...savedOrders];
        localStorage.setItem("orders", JSON.stringify(updatedOrders));
      }
      
    } catch (error) {
      console.error("주문 데이터 파싱 오류:", error);
    }
  }, [navigate]);

  // 금액 포맷 함수
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ko-KR").format(amount);
  };

  // 결제 수단 한글 표시
  const getPaymentMethodText = (method) => {
    const methodMap = {
      card: "신용카드",
      bank: "계좌이체",
      vbank: "가상계좌",
      phone: "휴대폰결제",
      kakao: "카카오페이",
      naverpay: "네이버페이",
      payco: "페이코",
      toss: "토스",
    };
    
    return methodMap[method] || method;
  };

  if (!orderData) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="warning">
          주문 정보를 찾을 수 없습니다. <Link to="/">홈으로 돌아가기</Link>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow-sm border-0 rounded-4 mb-4">
            <Card.Body className="text-center py-5">
              <FaCheckCircle size={64} className="mb-3 text-success" />
              <h1 className="h3 mb-3">결제가 완료되었습니다!</h1>
              <p className="text-muted mb-4">
                주문이 성공적으로 처리되었습니다. 감사합니다.
              </p>
              
              <div className="d-flex justify-content-center gap-3 mb-4">
                <Button
                  as={Link}
                  to="/my-orders"
                  variant="outline-primary"
                  style={{
                    borderColor: "var(--main-color)",
                    color: "var(--main-color)",
                    borderRadius: "100px",
                  }}
                >
                  <FaShoppingBag className="me-2" /> 주문 내역 보기
                </Button>
                <Button
                  as={Link}
                  to="/pray"
                  variant="primary"
                  style={{
                    backgroundColor: "var(--main-color)",
                    borderColor: "var(--main-color)",
                    borderRadius: "100px",
                  }}
                >
                  계속 쇼핑하기 <FaArrowRight className="ms-2" />
                </Button>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="shadow-sm border-0 rounded-4 mb-4">
            <Card.Header className="bg-white py-3 border-0">
              <h2 className="h5 mb-0">주문 상세 내역</h2>
            </Card.Header>
            <ListGroup variant="flush">
              {orderData.items.map((item, index) => (
                <ListGroup.Item key={index} className="py-3 border-bottom">
                  <div className="d-flex">
                    {/* 상품 이미지 */}
                    {item.image && (
                      <div
                        className="me-3"
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          backgroundImage: `url(${item.image})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          flexShrink: 0,
                        }}
                      />
                    )}

                    {/* 상품 정보 */}
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h3 className="h6 mb-1">{item.name}</h3>
                          <p className="text-muted small mb-0">
                            {item.templeName}
                          </p>
                        </div>
                        <div className="text-end">
                          <p className="mb-0" style={{ color: "var(--main-color)" }}>
                            {formatCurrency(item.price * item.quantity)}원
                          </p>
                          <small className="text-muted">
                            {formatCurrency(item.price)}원 × {item.quantity}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
            
            <Card.Footer className="bg-white border-0 py-3">
              <Row className="mb-2">
                <Col xs={6} className="text-muted">주문 번호:</Col>
                <Col xs={6} className="text-end">{orderData.receipt_id || "주문번호 없음"}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={6} className="text-muted">결제 수단:</Col>
                <Col xs={6} className="text-end">{getPaymentMethodText(orderData.paymentMethod)}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={6} className="text-muted">상품 금액:</Col>
                <Col xs={6} className="text-end">{formatCurrency(orderData.subtotal)}원</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={6} className="text-muted">할인 금액:</Col>
                <Col xs={6} className="text-end text-success">- {formatCurrency(orderData.discount)}원</Col>
              </Row>
              <Row className="mb-0">
                <Col xs={6} className="h5 mb-0">총 결제 금액:</Col>
                <Col xs={6} className="text-end h5 fw-bold" style={{ color: "var(--main-color)" }}>
                  {formatCurrency(orderData.total)}원
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderSuccessPage;